// MUNYA OS · navegación principal compartida (header desktop + tab bar móvil) · R0 responsive
// Única fuente de verdad de la matriz de visibilidad por rol (Mapa Visual v1.2 pág. 4 + #143/#149):
// · Dirección (studio_admin + studio_director): los 8 espacios
// · Gestión (studio_management = Marina): Calendario · Proyectos · Contabilidad · Catálogo · Almacén
// · Diseño (studio_designer): Calendario · Proyectos · Catálogo · Almacén
// · Gestoría externa (#46): SOLO Inicio + Contabilidad
// · Web: exclusivamente studio_admin, sin bypass de developer
// · developer: bypass en el resto de espacios

import {
  HouseSimple,
  CalendarBlank,
  Folders,
  Receipt,
  Users,
  Archive,
  GearSix,
  Tag,
  Globe,
  type Icon,
} from '@phosphor-icons/react';
import { useAuth } from '../lib/auth.js';
import { useKillswitch } from '../lib/feature-flags.js';
import { canAccessWebsite } from '../lib/website-access.js';

export interface NavItem {
  href: string;
  label: string;
  icon: Icon;
  roles?: string[];
}

export const FULL_NAV: NavItem[] = [
  { href: '/', label: 'Inicio', icon: HouseSimple },
  { href: '/calendario', label: 'Calendario', icon: CalendarBlank },
  { href: '/proyectos', label: 'Proyectos', icon: Folders },
  {
    href: '/contabilidad',
    label: 'Contabilidad',
    icon: Receipt,
    roles: ['studio_admin', 'studio_director', 'studio_management', 'external_gestoria'],
  },
  {
    href: '/crm',
    label: 'CRM',
    icon: Users,
    // Diseño NO accede al CRM (petición Jorge). Solo Dirección (+ developer por bypass).
    roles: ['studio_admin', 'studio_director'],
  },
  {
    href: '/crm/catalogo-servicios',
    label: 'Catálogo',
    icon: Tag,
    // #149 · accesible a todos los internos sin pasar por el CRM
    roles: ['studio_admin', 'studio_director', 'studio_designer', 'studio_management'],
  },
  { href: '/almacen', label: 'Almacén', icon: Archive },
  {
    href: '/web',
    label: 'Web',
    icon: Globe,
    roles: ['studio_admin'],
  },
  {
    href: '/administracion',
    label: 'Administración',
    icon: GearSix,
    roles: ['studio_admin', 'studio_director'],
  },
];

/** Navegación visible para el usuario actual (rol + gestoría + killswitch facturación). */
export function useVisibleNav(): NavItem[] {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const killInvoicing = useKillswitch('invoicing');
  const navBase = FULL_NAV.filter((item) =>
    item.href === '/web'
      ? canAccessWebsite(role)
      : !item.roles || role === 'developer' || item.roles.includes(role),
  );
  const navScoped =
    role === 'external_gestoria'
      ? navBase.filter((item) => item.href === '/' || item.href === '/contabilidad')
      : navBase;
  return navScoped.filter((item) => !(killInvoicing && item.href === '/contabilidad'));
}

/**
 * Href "más específico" que coincide con la ruta actual → solo ese se marca activo
 * (evita que /crm aparezca activo cuando estás en /crm/catalogo-servicios).
 */
export function activeHrefOf(items: NavItem[], pathname: string): string | null {
  const candidates = items.filter((it) =>
    it.href === '/' ? pathname === '/' : pathname === it.href || pathname.startsWith(it.href + '/'),
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (b.href.length > a.href.length ? b : a)).href;
}
